
import React from 'react';
import { useNavigate } from 'react-router-dom';

interface Destination {
  title: string;
  duration: string;
  description: string[];
  image: string;
  buttonText: string;
  buttonAction: string;
}

const destinations: Destination[] = [
  {
    title: "Hallstatt, Austria",
    duration: "Austria Country Side - Tour from 10 Days to 21 Days",
    description: [
      "A storybook alpine village with pastel-colored houses reflecting in a tranquil lake, surrounded by towering mountains.",
      "Famous for salt mines, skywalk viewpoints, and serene boat rides."
    ],
    image: "/images/europe-dest-1.jpg",
    buttonText: "Explore More",
    buttonAction: "explore"
  },
  {
    title: "Ronda, Spain",
    duration: "Around Ronda - Tour of 09 Night / 10 Days",
    description: [
      "A dramatic Andalusian town perched above a deep gorge, connected by the iconic Puente Nuevo bridge.",
      "Visitors can explore bullfighting history, Moorish gardens, and breathtaking cliffside views"
    ],
    image: "/images/europe-dest-2.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Plovdiv, Bulgaria",
    duration: "Amazing Bulgaria - Tour of 10 Night / 11 Days",
    description: [
      "One of Europe’s oldest continuously inhabited cities, blending Roman ruins, colorful 19th-century houses, and a vibrant arts scene.",
      "The Roman amphitheater is a must-see."
    ],
    image: "/images/europe-dest-3.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Kotor, Montenegro",
    duration: "Explore Montenegro - Tour of 10 Night / 11 Days",
    description: [
      "A medieval walled town nestled at the edge of the Adriatic fjord-like bay, offering cobblestone streets, ancient churches, and panoramic hikes to San Giovanni Fortress."
    ],
    image: "/images/europe-dest-4.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Giethoorn, Netherlands",
    duration: "Explore Giethoorn - Tour of 09 Night / 10 Days",
    description: [
      "Known as the “Venice of the North,” this charming Dutch village has no roads — only canals.",
      "Visitors can glide through waterways by boat and stroll along thatched-roof cottages."
    ],
    image: "/images/europe-dest-5.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Matera, Italy",
    duration: "Explore Giethoorn - Tour of 09 Night / 10 Days",
    description: [
      "A UNESCO-listed city of cave dwellings (“sassi”) carved into limestone cliffs.",
      "Matera combines ancient history with boutique cave hotels and a cinematic atmosphere."
    ],
    image: "/images/europe-dest-6.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Mostar, Bosnia & Herzegovina",
    duration: "Mostar, Bosnia & Herzegovina - Tour of 09 Night / 10 Days",
    description: [
      "Renowned for its Ottoman-era bridge (Stari Most), where daredevils leap into the turquoise river below.",
      "The old bazaar, mosques, and Turkish houses give it a rich cultural charm."
    ],
    image: "/images/europe-dest-7.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Faroe Islands, Denmark",
    duration: "Faroe Island, Denmakr - Tour of 09 Night / 10 Days",
    description: [
      "A remote archipelago between Iceland and Norway, known for dramatic cliffs, grass-roof houses, puffins, and untouched natural landscapes.",
      "Perfect for hikers and solitude seekers."
    ],
    image: "/images/europe-dest-8.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Český Krumlov, Czech Republic",
    duration: "Highlights of Cesky Krumlov - Tour of 09 Night / 10 Days",
    description: [
      "A fairytale town with a winding river, Renaissance castle, and medieval streets.",
      "Summer festivals, river rafting, and Baroque theater performances bring history to life."
    ],
    image: "/images/europe-dest-9.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Sibiu, Romania",
    duration: "Highlights of Romania - Tour of 09 Night / 10 Days",
    description: [
      "A charming Transylvanian town with pastel-colored squares, Gothic churches, and “houses with eyes” (unique roof windows).",
      "Surrounded by Carpathian landscapes and rich folklore."
    ],
    image: "/images/europe-dest-10.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  }
];

export const DestinationsSection: React.FC = () => {
  const navigate = useNavigate();

  const handleButtonClick = (action: string, title: string) => {
    if (action === 'enquire') {
      navigate(`/contact?subject=${encodeURIComponent('Enquiry about ' + title)}`);
      return;
    }

    if (action === 'explore') {
      navigate('/group-tours');
      return;
    }
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">

          </h2>
        </div>

        <div className="space-y-16">
          {destinations.map((destination, index) => (
            <div
              key={index}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
            >
              <div className={`space-y-6 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                <h3 className="text-2xl lg:text-3xl font-bold text-gray-900">
                  {destination.title}
                </h3>
                <p className="text-lg text-emerald-600 font-medium">
                  {destination.duration}
                </p>
                <ul className="space-y-3">
                  {destination.description.map((desc, descIndex) => (
                    <li key={descIndex} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></div>
                      <p className="text-gray-700 leading-relaxed">{desc}</p>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleButtonClick(destination.buttonAction, destination.title)}
                  className="inline-flex items-center px-6 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  {destination.buttonText}
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center ml-2"></i>
                </button>
              </div>

              <div className={`${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                <img
                  src={destination.image}
                  alt={destination.title}
                  className="w-full h-80 object-cover rounded-lg shadow-lg"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
